import React from 'react';
import { useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import { CircularProgress, Tooltip, IconButton, Fade, Grid2 as Grid, Collapse, Box, Typography, TablePagination } from "@mui/material";
import { FilterList, Refresh } from '@mui/icons-material';
import { cacheTableData, cacheTableFilter, cacheTablePage, cacheTableServer, cacheTableOrderBy, cacheTableRowsPerPage } from "../../Cache";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import { Link } from "react-router-dom";
import { dispatch } from '../../App';
import { ResetState } from '../../Reducer/TableReducer/TableSlice';
import "./TableComponent.css";

function TableComponent(props = {
    title: '',
    id: '',
    columns: [],
    promiseData: null,
    promiseServerData: null,
    height: '',
    minHeight: '',
    noCache: false,
    actionFirst: {},
    actionSecond: {},
    actionThird: {},
    sortOrder: {},
    actionSelection: {},
    onBeforeRefresh: false,
    search: true,
    mobileMode: 'table'
}) {
    const {
        id,
        sortOrder,
        mobileMode,
        promiseData,
        promiseServerData,
        noCache,
    } = props;
    const reloadTable = useSelector(state => state.TableReducer);
    const screenReducer = useSelector(state => state.ScreenReducer);
    const isMobileView = screenReducer.screen === 'XS' || screenReducer.screen === 'SM';
    const isCardMobileView = isMobileView && mobileMode === 'cards';

    const [data, setData] = React.useState(cacheTableData[id] ?? []);
    const [loading, setLoading] = React.useState(false);
    const [count, setCount] = React.useState(0);
    const [isOpenFilter, setIsOpenFilter] = React.useState(false);
    const defaultSortOrder = React.useMemo(
        () => cacheTableOrderBy[id] ?? sortOrder ?? { name: 'id', direction: 'desc' },
        [id, sortOrder]
    );
    const defaultPage = cacheTablePage[id] ?? 0;
    const defaultRowsPerPage = cacheTableRowsPerPage[id] ?? 25;
    const [mobilePage, setMobilePage] = React.useState(defaultPage);
    const [mobileRowsPerPage, setMobileRowsPerPage] = React.useState(defaultRowsPerPage);

    const ensureServerCache = React.useCallback((tableState = {}) => {
        if (!cacheTableServer[id]) {
            cacheTableServer[id] = {
                rowsPerPage: tableState.rowsPerPage ?? defaultRowsPerPage,
                page: tableState.page ?? defaultPage,
                searchText: tableState.searchText ?? '',
                sortOrder: tableState.sortOrder ? {
                    name: tableState.sortOrder.name,
                    direction: tableState.sortOrder.direction
                } : defaultSortOrder,
            };
        }

        return cacheTableServer[id];
    }, [defaultPage, defaultRowsPerPage, defaultSortOrder, id]);

    const onTable = React.useCallback((action = '', tableState = {}) => {
        if (promiseServerData) {
            if (action === 'tableInitialized') {
                const serverCache = ensureServerCache(tableState);
                serverCache.searchText = '';
            }

            if (
                action === 'reload' ||
                action === 'search' ||
                action === 'sort' ||
                action === 'filterChange' ||
                action === 'changePage' ||
                action === 'changeRowsPerPage'
            ) {
                const serverCache = ensureServerCache(tableState);

                if (action !== 'reload') {
                    serverCache.rowsPerPage = tableState.rowsPerPage ?? serverCache.rowsPerPage;
                    serverCache.page = tableState.page ?? serverCache.page;
                    serverCache.searchText = tableState.searchText ?? serverCache.searchText;
                    serverCache.sortOrder = tableState.sortOrder ? {
                        name: tableState.sortOrder.name,
                        direction: tableState.sortOrder.direction
                    } : serverCache.sortOrder;
                }

                setLoading(true);
                promiseServerData((data, count) => {
                    setLoading(false);
                    setData([...data]);
                    setCount(count);
                }, serverCache);
            }
        }
    }, [ensureServerCache, promiseServerData]);

    const loadData = React.useCallback(() => {
        if (promiseData) {
            setLoading(true);

            promiseData((data) => {
                setData([...data]);
                setLoading(false);
                if (!noCache) cacheTableData[id] = [...data];
            });
        }
        else if (promiseServerData) {
            onTable('reload');
        }
    }, [id, noCache, onTable, promiseData, promiseServerData]);

    const hasActionFirst = !!props.actionFirst && !!(props.actionFirst.label || props.actionFirst.link || props.actionFirst.onClick);
    const hasActionSecond = !!props.actionSecond && !!(props.actionSecond.label || props.actionSecond.onClick);
    const hasActionThird = !!props.actionThird && !!(props.actionThird.label || props.actionThird.onClick);

    const renderTableActions = () => (
        <>
            {hasActionThird && <ButtonComponent color={'#28a745'} className="buttonActionThird" label={props.actionThird.label} onClick={props.actionThird.onClick} />}
            {
                hasActionSecond && <ButtonComponent
                    loading={props.actionSecond.loading}
                    color={props.actionSecond.color ?? '#35A2EB'}
                    className="buttonActionSecond"
                    outlined={props.actionSecond.outlined}
                    label={props.actionSecond.label}
                    onClick={props.actionSecond.onClick}
                    disabled={props.actionSecond.disabled}
                />
            }
            {
                hasActionFirst && (
                    props.actionFirst.link ? (
                        <Link className="TableMobileCardsToolbarLink" to={props.actionFirst.link}>
                            <ButtonComponent className="buttonActionFirst" label={props.actionFirst.label} />
                        </Link>
                    ) : (
                        <ButtonComponent className="buttonActionFirst" label={props.actionFirst.label} onClick={props.actionFirst.onClick} />
                    )
                )
            }
        </>
    );

    const options = {
        responsive: isMobileView ? 'vertical' : 'standard',

        // Actions
        download: false,
        search: props.search !== false,
        filter: false,
        print: false,
        customToolbar: () => {
            return (
                <>
                    {props.filters && (
                        <Tooltip title={"Afficher les filtres"}>
                            <IconButton
                                className={isOpenFilter ? "active" : ""}
                                onClick={() => setIsOpenFilter(!isOpenFilter)}>
                                <FilterList />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!props.noReload && <Tooltip title={"Recharger la liste"}>
                        <IconButton onClick={() => {
                            if (props.onBeforeRefresh) props.onBeforeRefresh();
                            loadData();
                        }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>}
                    {renderTableActions()}
                </>
            );
        },

        // Traduction
        textLabels: {
            body: {
                noMatch: loading ? 'Chargement...' : 'Aucun résultat.',
                toolTip: "Trier",
                columnHeaderTooltip: column => `Trier par ${column.label}`
            },
            pagination: {
                next: "Page suivante",
                previous: "Page précédente",
                rowsPerPage: "Ligne par page:",
                displayRows: "sur"
            },
            toolbar: {
                search: "Recherche",
                downloadCsv: "Télécharger CSV",
                print: "Imprimer",
                viewColumns: "Colonnes",
                filterTable: "Filtre"
            },
            viewColumns: {
                title: "Affichage des colonnes",
                titleAria: "Afficher/Cacher colonne"
            },
            selectedRows: {
                text: "ligne(s) sélectionnée(s)",
                delete: "Supprimer"
            },
            filter: {
                all: "Tous",
                title: "FILTRES",
                reset: "RESET"
            }
        },

        // Cache
        onFilterChange: (column, list) => {
            cacheTableFilter[props.id] = list;
        },
        onChangePage: (number) => {
            cacheTablePage[props.id] = number;
        },
        onColumnSortChange: (changedColumn, direction) => {
            cacheTableOrderBy[props.id] = { name: changedColumn, direction: direction };
        },
        onChangeRowsPerPage: (numberOfRows) => {
            cacheTableRowsPerPage[props.id] = numberOfRows;
        },
        sortOrder: defaultSortOrder,
        page: defaultPage,
        rowsPerPage: defaultRowsPerPage,
        rowsPerPageOptions: [10, 25, 50, 100],

        // Sélection
        selectableRowsHeader: !!props.actionSelection,
        selectableRowsHideCheckboxes: false,
        selectableRowsOnClick: false,
        selectableRows: (props.actionSelection) ? 'multiple' : 'none',
        customToolbarSelect: () => {
            return <>
                {props.actionSelection && <ButtonComponent className="buttonSelection" label={props.actionSelection.label} onClick={props.actionSelection.onClick} />}
            </>;
        },
        onRowsDelete: false,
        isRowSelectable: () => {
            return true;
        },
        rowsSelected: [],

        // Serveur
        count: count,
        serverSide: !!props.promiseServerData,
        onTableInit: onTable,
        onTableChange: onTable
    };

    React.useEffect(() => {
        loadData();
    }, [loadData]);
    React.useEffect(() => {
        if (reloadTable.id === id) {
            loadData();
            dispatch(ResetState());
        }
    }, [id, loadData, reloadTable]);
    React.useEffect(() => {
        if (!props.promiseServerData) {
            const maxPage = Math.max(0, Math.ceil(data.length / mobileRowsPerPage) - 1);
            if (mobilePage > maxPage) {
                setMobilePage(maxPage);
                cacheTablePage[props.id] = maxPage;
            }
        }
    }, [data, mobilePage, mobileRowsPerPage, props.id, props.promiseServerData]);

    if (cacheTableFilter[props.id]) {
        for (let index in props.columns) {
            props.columns[index].options.filterList = cacheTableFilter[props.id][index];
        }
    }

    let classNames = 'MUIDataTable';
    if (props.small) classNames += ' small';
    if (props.noShadow) classNames += ' noShadow';
    if (isMobileView) classNames += ' mobileView';

    let style = props.style ? { ...props.style } : {};
    if (props.margin) style.margin = props.margin;
    else style.margin = 0;
    const mobileCount = props.promiseServerData ? count : data.length;
    const mobileData = props.promiseServerData ? data : data.slice(
        mobilePage * mobileRowsPerPage,
        mobilePage * mobileRowsPerPage + mobileRowsPerPage
    );

    const handleMobilePageChange = (_event, page) => {
        setMobilePage(page);
        cacheTablePage[props.id] = page;

        if (props.promiseServerData) {
            onTable('changePage', {
                page: page,
                rowsPerPage: mobileRowsPerPage,
                searchText: cacheTableServer[props.id]?.searchText ?? '',
                sortOrder: cacheTableServer[props.id]?.sortOrder ?? defaultSortOrder
            });
        }
    };

    const handleMobileRowsPerPageChange = (event) => {
        const value = parseInt(event.target.value, 10);
        const nextRowsPerPage = Number.isNaN(value) ? defaultRowsPerPage : value;

        setMobileRowsPerPage(nextRowsPerPage);
        setMobilePage(0);
        cacheTableRowsPerPage[props.id] = nextRowsPerPage;
        cacheTablePage[props.id] = 0;

        if (props.promiseServerData) {
            onTable('changeRowsPerPage', {
                page: 0,
                rowsPerPage: nextRowsPerPage,
                searchText: cacheTableServer[props.id]?.searchText ?? '',
                sortOrder: cacheTableServer[props.id]?.sortOrder ?? defaultSortOrder
            });
        }
    };

    const isMobileRenderableValue = (value) => {
        if (value === undefined || value === null || value === '') return false;
        if (React.isValidElement(value)) return true;
        if (Array.isArray(value)) return value.every((item) => isMobileRenderableValue(item));

        const primitiveType = typeof value;
        return primitiveType === 'string' || primitiveType === 'number' || primitiveType === 'boolean';
    };

    return (
        <Fade in={true} timeout={1000}>
            <div style={{ height: '100%', background: isCardMobileView ? '' : '#fff' }}>
                <Grid container>
                    <Collapse in={isOpenFilter} sx={{ width: "100%" }}>
                        <Grid container spacing={2} sx={{ mx: 0.5, my: 0.5, py: 1, px: 1, boxShadow: 'inset 0px 0px 2px 0px rgba(0, 0, 0, 0.2)', borderRadius: '8px'}} >
                            {props.filters}
                        </Grid>
                    </Collapse>
                    <Grid size={{ xs: 12 }}>
                        {isCardMobileView ? (
                            <Box className="TableMobileCards">
                                {props.title && <Typography className="TableMobileCardsTitle">{props.title}</Typography>}
                                {(props.filters || !props.noReload || hasActionFirst || hasActionSecond || hasActionThird) && (
                                    <Box className="TableMobileCardsToolbar">
                                        {props.filters && (
                                            <Tooltip title={"Afficher les filtres"}>
                                                <IconButton
                                                    className={isOpenFilter ? "active" : ""}
                                                    onClick={() => setIsOpenFilter(!isOpenFilter)}
                                                >
                                                    <FilterList />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!props.noReload && (
                                            <Tooltip title={"Recharger la liste"}>
                                                <IconButton onClick={() => {
                                                    if (props.onBeforeRefresh) props.onBeforeRefresh();
                                                    loadData();
                                                }}>
                                                    <Refresh />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {renderTableActions()}
                                    </Box>
                                )}
                                {(props.loading || (loading && !props.hideLoading)) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                                        <CircularProgress size={18} className="circularProgress" />
                                        <Typography sx={{ fontSize: 12, color: 'var(--greytext)' }}>Mise à jour des données.</Typography>
                                    </Box>
                                )}
                                {!(props.loading || loading) && data.length < 1 && (
                                    <Box className="TableMobileCard">
                                        <Typography sx={{ fontSize: 12, color: '#7b8798' }}>Aucun résultat.</Typography>
                                    </Box>
                                )}
                                {mobileData.map((row, index) => (
                                    <Box key={`mobile-card-${index}`} className="TableMobileCard">
                                        {props.columns
                                            .filter((col) => col && col.name && !(col.options && col.options.display === 'excluded'))
                                            .map((col) => {
                                                const value = row[col.name];
                                                const displayedValue = typeof col.options?.customBodyRender === 'function'
                                                    ? col.options.customBodyRender(value)
                                                    : value;
                                                const isComplexDisplayedValue = React.isValidElement(displayedValue) || Array.isArray(displayedValue);

                                                if (!isMobileRenderableValue(displayedValue)) return null;

                                                return (
                                                    <Box
                                                        key={`${index}-${col.name}`}
                                                        className={`TableMobileCardRow${isComplexDisplayedValue ? ' TableMobileCardRowComplex' : ''}`}
                                                    >
                                                        <Typography className="TableMobileCardLabel">{col.label}</Typography>
                                                        <Box className={`TableMobileCardValue${isComplexDisplayedValue ? ' TableMobileCardValueComplex' : ''}`}>
                                                            {displayedValue}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                    </Box>
                                ))}
                                <TablePagination
                                    component="div"
                                    count={mobileCount}
                                    page={mobilePage}
                                    rowsPerPage={mobileRowsPerPage}
                                    onPageChange={handleMobilePageChange}
                                    onRowsPerPageChange={handleMobileRowsPerPageChange}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    labelRowsPerPage="Ligne par page:"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                                />
                            </Box>
                        ) : (
                            <MUIDataTable
                                style={style}
                                className={classNames}
                                data={data}
                                columns={props.columns}
                                options={options}
                                title={
                                    props.loading || (loading && !props.hideLoading) ? (
                                        <>
                                            <CircularProgress size={20} className="circularProgress" />
                                            <span className="span">Mise à jour des données.</span>
                                        </>
                                    ) : props.title ? (
                                        <span className="title">{props.title}</span>
                                    ) : ''
                                }
                            />
                        )}
                    </Grid>
                </Grid>
            </div>
        </Fade>
    );
}

export default TableComponent;
